import { getReplenishmentApprovals, getReplenishmentInstrumentEvents, type InstrumentActual, type InstrumentEvent, type InstrumentRow } from '@api/replenishment-engine';
import DataSheet, { type DataSheetColumn } from '@components/ui/DataSheet';
import { Group, SegmentedControl, Select, Stack, Text } from '@mantine/core';
import { formatCount } from '@utils/currency';
import { useCallback, useEffect, useMemo, useState, type FC, type ReactElement, type ReactNode } from 'react';

import ExplainWindow from '../ExplainWindow';
import { dayLabel, localDateKey, monthEndKey, monthStartKey } from './dates';
import classes from './engine.module.css';
import { TYPE_LABEL } from './status';
import { Lines, StatusBadge } from './tables';

const n = (v: number, maxDecimals = 1): string => formatCount(v, { maxDecimals });

type By = 'DIP' | 'LAND' | 'APPROVED';

const DONE_LABEL: Record<InstrumentActual['kind'], string> = { PRODUCED: 'produced', INWARDED: 'inwarded', RECEIVED: 'received', MOVED: 'moved' };

/**
 * Everything saved or approved: in flight today, and the history of what was decided against
 * which period's demand. Reads the frozen requirement on each instrument, so it is true however
 * many runs have happened since; the Done column is read from the books (stock events, orders,
 * STNs) each time, so it moves as the warehouse works.
 */
const TrackPanel: FC = () => {
    const today = localDateKey();
    const [month, setMonth] = useState<string>(monthStartKey(localDateKey()));
    const monthOptions = useMemo(() => {
        const out: Array<{ value: string; label: string }> = [];
        const base = new Date();
        for (let i = -3; i <= 8; i += 1) {
            const d = new Date(base.getFullYear(), base.getMonth() + i, 1);
            out.push({ value: localDateKey(d), label: d.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' }) });
        }
        return out;
    }, []);
    const [by, setBy] = useState<By>('APPROVED');
    const [rows, setRows] = useState<InstrumentRow[]>([]);
    const [loading, setLoading] = useState(false);
    const [detail, setDetail] = useState<{ row: InstrumentRow; events: InstrumentEvent[] } | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            setRows(await getReplenishmentApprovals(monthStartKey(month), monthEndKey(month), by));
        } finally {
            setLoading(false);
        }
    }, [month, by]);
    useEffect(() => {
        void load();
    }, [load]);

    const open = async (row: InstrumentRow): Promise<void> => {
        const events = await getReplenishmentInstrumentEvents(row.id).catch(() => []);
        setDetail({ row, events });
    };
    // A save is one objective's plan for one period: the lines saved together. Picking one
    // reads it back whole, "what you were told for that period", against what got done.
    const planOf = (r: InstrumentRow): string | null => {
        if (r.status !== 'SAVED') return null;
        const p = r.reason?.savedPeriod as { start: string; end: string } | null | undefined;
        return `${p ? `${p.start}|${p.end}` : 'none'}|${r.approvedAt?.slice(0, 10) ?? ''}`;
    };
    const plans = useMemo(() => {
        const out = new Map<string, { label: string; lines: number; planned: number; done: number }>();
        for (const r of rows) {
            const key = planOf(r);
            if (!key) continue;
            const [start, end, savedOn] = key.split('|');
            const cur = out.get(key) ?? { label: `${start === 'none' ? 'No period' : start === end ? dayLabel(start) : `${dayLabel(start)} → ${dayLabel(end)}`} · saved ${dayLabel(savedOn)}`, lines: 0, planned: 0, done: 0 };
            cur.lines += 1;
            cur.planned += r.approvedQty ?? r.qty;
            cur.done += r.actual?.doneQty ?? 0;
            out.set(key, cur);
        }
        return out;
    }, [rows]);
    const [plan, setPlan] = useState<string | null>(null);
    const shown = useMemo(() => (plan ? rows.filter((r) => planOf(r) === plan) : rows), [rows, plan]);
    const picked = plan ? plans.get(plan) : undefined;

    const columns = useMemo<DataSheetColumn<InstrumentRow>[]>(
        () => [
            { key: 'sku', header: 'SKU', width: 200, sticky: true, sortable: true, valueAccessor: (r) => r.sku, render: (_v, r): ReactElement => <Lines lines={[{ text: r.sku, strong: true }, { text: '', sub: `${TYPE_LABEL[r.type] ?? r.type} · ${r.itemName ?? r.itemId}` }]} /> },
            { key: 'status', header: 'Status', width: 110, sortable: true, filterable: true, valueAccessor: (r) => r.status, render: (_v, r): ReactElement => <StatusBadge status={r.status} /> },
            { key: 'qty', header: 'Planned qty', width: 110, align: 'right', sortable: true, valueAccessor: (r) => r.approvedQty ?? r.qty, render: (_v, r): ReactElement => <Text size="sm" fw={600}>{n(r.approvedQty ?? r.qty, r.unit ? 1 : 0)}</Text> },
            { key: 'vendor', header: 'Vendor / factory', width: 180, filterable: true, valueAccessor: (r) => r.vendorName ?? r.fromFacilityId ?? '', render: (_v, r): ReactElement => <Text size="sm">{r.vendorName ?? r.fromFacilityId ?? '—'}</Text> },
            { key: 'placeBy', header: 'Planned start', width: 120, sortable: true, valueAccessor: (r) => r.placeBy, render: (_v, r): ReactElement => <Lines lines={[{ text: dayLabel(r.placeBy), late: r.placeBy <= today && (r.status === 'APPROVED' || r.status === 'SCHEDULED') }]} /> },
            { key: 'lands', header: 'Expected by', width: 170, sortable: true, valueAccessor: (r) => r.expectedLandDate ?? r.landDate, render: (_v, r): ReactElement => <Lines lines={[{ text: `${dayLabel(r.landDate)}${r.expectedLandDate ? ` · ${dayLabel(r.expectedLandDate)}` : ''}`, sub: r.expectedLandDate && r.expectedLandDate > r.landDate ? `slips ${Math.round((new Date(r.expectedLandDate).getTime() - new Date(r.landDate).getTime()) / 86400000)} days` : '', tone: 'red' }]} /> },
            { key: 'doc', header: 'Document ID', width: 150, render: (_v, r): ReactElement => <Text size="sm" c={r.documentCode ? undefined : 'dimmed'}>{r.documentCode ?? 'not placed yet'}</Text> },
            {
                key: 'done',
                header: 'Done',
                width: 110,
                align: 'right',
                sortable: true,
                valueAccessor: (r) => r.actual?.doneQty ?? 0,
                // Just the quantity; when and how it got done is in the row's detail window.
                render: (_v, r): ReactElement => (r.actual ? <Text size="sm" fw={600}>{n(r.actual.doneQty, 0)}</Text> : <Text size="sm" c="dimmed">—</Text>),
            },
            {
                key: 'why',
                header: 'Saved / approved for',
                width: 250,
                render: (_v, r): ReactElement => {
                    const s = (r.requirementSnapshot ?? {}) as { dipDate?: string | null; zeroDate?: string | null; landDate?: string | null; requiredQty?: number };
                    const period = r.reason?.savedPeriod as { start: string; end: string } | null | undefined;
                    const verb = r.status === 'SAVED' ? 'saved' : 'approved';
                    return <Lines lines={[{ text: `dip ${dayLabel(s.dipDate)} · zero ${dayLabel(s.zeroDate)}`, sub: `${r.approvedAt ? `${verb} ${dayLabel(r.approvedAt.slice(0, 10))}` : ''}${period ? ` for ${dayLabel(period.start)} → ${dayLabel(period.end)}` : ''}${s.requiredQty != null ? ` · needed ${n(s.requiredQty, 0)}` : ''}` }]} />;
                },
            },
        ],
        [today],
    );
    // The grid's own total row: sums what is on screen, so filters and search move it.
    const totals = useCallback(
        (visible: InstrumentRow[]): Partial<Record<string, ReactNode>> => ({
            sku: 'Total',
            qty: n(visible.reduce((sum, r) => sum + (r.approvedQty ?? r.qty), 0), 0),
            done: n(visible.reduce((sum, r) => sum + (r.actual?.doneQty ?? 0), 0), 0),
        }),
        [],
    );

    return (
        <Stack gap="sm">
            <Group gap="sm" wrap="wrap">
                <Select size="xs" w={180} value={month} data={monthOptions} onChange={(v) => v && setMonth(v)} />
                <SegmentedControl size="xs" value={by} onChange={(v) => setBy(v as By)} data={[{ value: 'APPROVED', label: 'Saved or approved in' }, { value: 'DIP', label: 'Demand dipping in' }, { value: 'LAND', label: 'Landing in' }]} />
                <Select size="xs" w={260} clearable placeholder="Every line" value={plan && plans.has(plan) ? plan : null} data={[...plans.entries()].map(([value, p]) => ({ value, label: `Saved plan · ${p.label}` }))} onChange={setPlan} />
                {picked && (
                    <Text size="xs" c="dimmed">
                        {picked.lines} lines · {n(picked.planned, 0)} planned · {n(picked.done, 0)} done ({picked.planned > 0 ? Math.round((picked.done / picked.planned) * 100) : 0}%)
                    </Text>
                )}
            </Group>
            <DataSheet<InstrumentRow> data={shown} columns={columns} isLoading={loading} getRowKey={(r) => r.id} rowHeight={52} aggregateFn={totals} enableSearch searchPlaceholder="Search SKU, material, vendor…" title="Saved & approved" subtitle={`${shown.length} rows · saved copies are frozen numbers; Done is read from the books`} enableColumnPicker columnPickerKey="replen_track_cols_v1" onRowClick={(r) => void open(r)} />
            {detail && (
                <ExplainWindow opened onClose={() => setDetail(null)} title={`${detail.row.sku} · ${TYPE_LABEL[detail.row.type] ?? detail.row.type}`} subtitle={detail.row.itemName}>
                    <Stack gap="sm" p="md">
                        {detail.row.actual && (
                            <>
                                <Text size="sm" fw={600}>
                                    What got done
                                </Text>
                                <Text size="xs" c="dimmed">
                                    {n(detail.row.actual.doneQty, 0)} {DONE_LABEL[detail.row.actual.kind]} between {dayLabel(detail.row.actual.windowStart)} and {dayLabel(detail.row.actual.windowEnd)}
                                    {detail.row.actual.orderedQty != null ? ` · ${n(detail.row.actual.orderedQty, 0)} put on orders` : ''}
                                    {detail.row.actual.firstDate ? ` · first ${dayLabel(detail.row.actual.firstDate)}, last ${dayLabel(detail.row.actual.lastDate)}` : ''}
                                </Text>
                            </>
                        )}
                        <Text size="sm" fw={600}>
                            {detail.row.status === 'SAVED' ? 'What was saved' : 'What was approved'}
                        </Text>
                        <Text size="xs" c="dimmed" component="pre" className={classes.line}>
                            {JSON.stringify(detail.row.requirementSnapshot ?? detail.row.reason, null, 2)}
                        </Text>
                        <Text size="sm" fw={600}>
                            History
                        </Text>
                        {detail.events.length === 0 && (
                            <Text size="xs" c="dimmed">
                                No events yet.
                            </Text>
                        )}
                        {detail.events.map((e) => (
                            <div key={e.id} className={classes.step}>
                                <span className={classes.stepIndex}>•</span>
                                <div className={classes.lines}>
                                    <div className={classes.line}>{e.event.toLowerCase()}</div>
                                    <div className={classes.sub}>{e.payload ? JSON.stringify(e.payload) : ''}</div>
                                </div>
                                <Text size="xs" c="dimmed">
                                    {new Date(e.at).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                                </Text>
                            </div>
                        ))}
                    </Stack>
                </ExplainWindow>
            )}
        </Stack>
    );
};

export default TrackPanel;
